import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Search, Users, X } from 'lucide-react';
import { posApi, reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
// import GroupByToggle from '@/components/reports/GroupByToggle';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

interface ReportCustomer {
  name: string;
  customer_name: string;
  mobile?: string;
}

export default function PartySaleSummarySection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [groupBy] = useState<'Invoice' | 'Item'>('Invoice');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const { data: customersRes } = useQuery({
    queryKey: ['customersForPartySaleSummary', customerSearch],
    queryFn: async () => {
      const res = await posApi.getCustomers({ search: customerSearch, limit: 50 });
      return res.data.message;
    },
  });

  const customers: ReportCustomer[] = customersRes?.customers || customersRes || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer?: ReportCustomer) => {
    if (!customer) {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    } else {
      setSelectedCustomer(customer.name);
      setSelectedCustomerLabel(
        customer.mobile ? `${customer.customer_name} - ${customer.mobile}` : customer.customer_name
      );
    }
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['partySaleSummary', from_date, to_date, selectedCustomer, groupBy],
    queryFn: () =>
      reportsApi.getPartySaleSummary({
        from_date,
        to_date,
        customer: selectedCustomer || undefined,
        group_by: groupBy,
      }),
  });

  useEffect(() => {
    setPage(0);
  }, [rows, from_date, to_date, selectedCustomer, groupBy]);

  const handleExport = () => {
    if (!rows.length) return;
    if (groupBy === 'Item') {
      downloadCsv(
        `party-sale-summary-item-${from_date}-${to_date}.csv`,
        [
          { key: 'customer_name', label: 'Customer' },
          { key: 'invoice', label: 'Invoice' },
          { key: 'date', label: 'Date' },
          { key: 'item_name', label: 'Item' },
          { key: 'qty', label: 'Qty' },
          { key: 'rate', label: 'Rate' },
          { key: 'amount', label: 'Amount' },
        ],
        rows as Record<string, unknown>[]
      );
    } else {
      downloadCsv(
        `party-sale-summary-${from_date}-${to_date}.csv`,
        [
          { key: 'customer_name', label: 'Customer' },
          { key: 'total_invoices', label: 'Total Invoices' },
          { key: 'total_qty', label: 'Total Qty' },
          { key: 'gross_sales', label: 'Gross Sales' },
          { key: 'discount', label: 'Discount' },
          { key: 'tax', label: 'Tax' },
          { key: 'net_sales', label: 'Net Sales' },
          { key: 'amount_received', label: 'Amount Received' },
          { key: 'outstanding', label: 'Outstanding' },
        ],
        rows as Record<string, unknown>[]
      );
    }
  };

  const pageRows = rows.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE);

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={customerDropdownRef}>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              Customer
            </label>
            {selectedCustomer ? (
              <div className="input-field flex items-center justify-between gap-2 py-2">
                <span className="truncate text-sm text-gray-900">{selectedCustomerLabel}</span>
                <button
                  type="button"
                  onClick={() => handleSelectCustomer()}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  aria-label="Clear customer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="input-field pl-9"
                    placeholder={t('reports.searchByNamePhone')}
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                </div>
                {showCustomerDropdown && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer()}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 text-gray-700"
                    >
                      All Customers
                    </button>
                    {customers.length > 0 ? (
                      customers.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => handleSelectCustomer(c)}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50"
                        >
                          <p className="font-medium text-gray-900">{c.customer_name}</p>
                          {c.mobile && <p className="text-xs text-gray-500">{c.mobile}</p>}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">No customers found</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {/* <GroupByToggle groupBy={groupBy} onChange={setGroupBy} /> */}
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[420px]">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : rows.length > 0 ? (
          groupBy === 'Item' ? (
            <table className="w-full text-sm text-left min-w-[720px]">
              <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Invoice</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Item</th>
                  <th className="px-4 py-3 font-medium text-right">Qty</th>
                  <th className="px-4 py-3 font-medium text-right">Rate</th>
                  <th className="px-4 py-3 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((row: any, i: number) => (
                  <tr key={`${row.invoice}-${row.item}-${i}`} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-gray-900">{row.customer_name}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{row.invoice}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {row.date ? format(new Date(row.date), 'd MMM yyyy') : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-900">{row.item_name || row.item}</td>
                    <td className="px-4 py-3 text-right">{row.qty}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.rate)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatMoney(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm text-left min-w-[900px]">
              <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('reports.customer')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.totalInvoices')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.totalQty')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.grossSales')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('common.discount')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('common.tax')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.netSales')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.amountReceived')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.outstanding')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((row: any) => (
                  <tr key={row.customer} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-gray-900">{row.customer_name}</td>
                    <td className="px-4 py-3 text-right">{row.total_invoices}</td>
                    <td className="px-4 py-3 text-right">{row.total_qty}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(row.gross_sales)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.discount)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.tax)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatMoney(row.net_sales)}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(row.amount_received)}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(row.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">{t('reports.noPartySales')}</p>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={rows.length} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
